import { useCallback, useEffect, useState } from 'react';
import { graphql } from 'react-relay';
import type { GraphQLError } from 'graphql';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { isStatusCodeError } from '@confluence/error-types/entry-points/isStatusCodeError.ts';

import { NO_CLASSIFICATION } from '../constants/NoClassification.ts';
import type { ClassificationLevel } from '../ClassificationRadioOption.tsx';
import { Status } from '../constants/Status.ts';

import type {
	useClassificationChangeStateQuery$data,
	useClassificationChangeStateQuery$variables,
} from './__generated__/useClassificationChangeStateQuery.graphql.ts';
import { useContentClassification } from './useContentClassification.ts';
import { useOrgDefaultClassificationLevelId } from './useOrgDefaultClassificationLevelId.ts';

const UseClassificationChangeStateQuery = graphql`
	query useClassificationChangeStateQuery($spaceKey: String!) {
		space(key: $spaceKey) {
			id
			name
			defaultClassificationLevelId
			metadata {
				totalCurrentPages
			}
		}
		classificationLevels {
			id
			name
			description
			color
			order
			status
			guideline
			guidelineADF
		}
	}
`;

export const useClassificationChangeState = (
	contentId: string,
	selectedSpaceKey?: string,
	currentSpaceKey?: string | null,
): {
	classificationChanged: boolean;
	selectedClassification: string;
	selectedSpaceClassification: ClassificationLevel | null;
	currentClassification: string;
	classificationLoading: boolean;
	classificationError: Error | undefined;
} => {
	const [unhandledError, setUnhandledError] = useState<Error>();
	const [selectedSpaceClassification, setSelectedSpaceClassification] =
		useState<ClassificationLevel | null>(null);

	// Mark 404 errors, when space data classification isn't assigned, as handled since it's expected
	// Set unhandled error for all other errors
	const processError = useCallback((error: ApolloErrorInterop | GraphQLError) => {
		if (isStatusCodeError(error, '404')) {
			markErrorAsHandled(error);
		} else {
			setUnhandledError(error);
		}
	}, []);

	/*
    Fetch classification for current page
    Currently no error handling for content classification since the behavior is to
    ignore content classification and use space default classification if there's an error.

    If intended behavior changes in the future, we'll need to handle the error.
  */
	const {
		classification: currentClassification,
		contentLevelLoading: currentClassificationLoading,
	} = useContentClassification({
		contentId,
		spaceKeyOverride: currentSpaceKey,
	});

	// fetch classification for target space
	// default target space is current space
	const {
		data: selectedSpaceData,
		loading: selectedSpaceLoading,
		error: selectedSpaceError,
		refetch: selectedSpaceRefetch,
	} = useQueryInterop<
		useClassificationChangeStateQuery$data,
		useClassificationChangeStateQuery$variables
	>(UseClassificationChangeStateQuery, {
		variables: { spaceKey: currentSpaceKey || '' },
		skip: !currentSpaceKey,
		errorPolicy: 'all',
		fetchPolicy: 'network-only',
		notifyOnNetworkStatusChange: true,
	});

	const {
		orgDefaultClassificationLevelId,
		orgDefaultClasificationLoading,
		orgDefaultClassificationError,
	} = useOrgDefaultClassificationLevelId({ skip: !currentSpaceKey });

	// if selected space changes, load/refetch query
	useEffect(() => {
		if (!selectedSpaceKey) return;

		// reset unhandled error (so if one exists, it'll cleared when the selected space changes)
		setUnhandledError(undefined);

		// reset selected space classification when selected space changes
		// causes additional rerender but necessary to avoid displaying wrong classification for space
		setSelectedSpaceClassification(null);
		selectedSpaceRefetch({ spaceKey: selectedSpaceKey }).catch(processError);
	}, [selectedSpaceKey, selectedSpaceRefetch, processError]);

	// handle selected space data updates
	useEffect(() => {
		if (selectedSpaceLoading || orgDefaultClasificationLoading) {
			return;
		}

		if (selectedSpaceError) {
			processError(selectedSpaceError);
			if (!orgDefaultClassificationLevelId) return;
		}

		if (orgDefaultClassificationError) {
			processError(orgDefaultClassificationError);
			return;
		}

		const selectedSpaceClassificationId =
			selectedSpaceData?.space?.defaultClassificationLevelId ?? orgDefaultClassificationLevelId;
		const selectedSpaceClassificationLevels = selectedSpaceData?.classificationLevels || [];

		const selectedSpaceClassification =
			selectedSpaceClassificationLevels.find(
				(level) => level?.id === selectedSpaceClassificationId,
			) || null;

		// type error from converting `color: string` to `color: Color`, so safe to cast
		setSelectedSpaceClassification(selectedSpaceClassification as ClassificationLevel);
	}, [
		selectedSpaceData,
		selectedSpaceLoading,
		selectedSpaceError,
		processError,
		orgDefaultClassificationLevelId,
		orgDefaultClasificationLoading,
		orgDefaultClassificationError,
	]);

	// retrieve classification name of selected space. Otherwise it's NO_CLASSIFICATION
	const selectedSpaceClassificationName =
		selectedSpaceClassification?.status === Status.PUBLISHED
			? selectedSpaceClassification?.name || NO_CLASSIFICATION
			: NO_CLASSIFICATION;
	// retrieve classification name of current space. Otherwise it's NO_CLASSIFICATION
	const currentClassificationName =
		currentClassification?.status === Status.PUBLISHED
			? currentClassification?.name || NO_CLASSIFICATION
			: NO_CLASSIFICATION;

	/*
	 * evaluates if classification changed when new space was selected
	 * Changed if selected space is different from current space
	 * AND
	 * if published classification id is different from current published classification id
	 * if classification isn't published (e.g. archived/deleted), then it's considered as NO_CLASSIFICATION
	 *
	 * so if selected space classification is archived, and current classification is also archived, it's not considered as changed
	 */
	const classificationChanged =
		!!selectedSpaceKey &&
		selectedSpaceKey !== currentSpaceKey &&
		(selectedSpaceClassification?.status === Status.PUBLISHED ||
			currentClassification?.status === Status.PUBLISHED) &&
		selectedSpaceClassification?.id !== currentClassification?.id;

	return {
		classificationChanged,
		selectedClassification: selectedSpaceClassificationName,
		selectedSpaceClassification,
		currentClassification: currentClassificationName,
		classificationLoading:
			selectedSpaceLoading || currentClassificationLoading || orgDefaultClasificationLoading,
		classificationError: unhandledError,
	};
};
